import React from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";

// --- Layouts ---
import GuestLayout from "./components/guestLayout";
import FunctionalLayout from "./components/functionalLayout";
import DefaultLayout from "./components/DefaultLayout";

// --- Auth / Registration ---
import SelectLanguage from "./Pages/Authentications/Registration/selectLanguage";
import SelectRole from "./Pages/Authentications/Registration/SelectRole";
import PersonalDetails from "./Pages/Authentications/Registration/PersonalDetails";
import Otp1 from "./Pages/Authentications/Registration/Otp1";
import Otp2 from "./Pages/Authentications/Registration/Otp2";
import SetPassword from "./Pages/Authentications/Registration/SetPassword";
import RegisterCompany from "./Pages/Authentications/Registration/RegisterCompany";
import RegisteredSuccessfully from "./Pages/Authentications/Registration/registeredSuccessfully";
import PaymentSuccess from "./Pages/Authentications/Registration/subscriptionSuccess";
import OrderSummaryForAll from "./Pages/Authentications/Registration/orderSummary/OrderSummaryForAll";
import EmailLogin from "./Pages/Authentications/Login/EmailLogin";
import OtpLogin from "./Pages/Authentications/Login/OtpLogin";
import DeleteLeader from "./Pages/Authentications/deleteLeader/DeleteLeader";

// --- Main App Pages ---
import Dashboard from "./Pages/Home/DashBoard/Dashboard";
import MyTeam from "./Pages/Home/MyTeam/myTeam";
import CreateTeam from "./Pages/Home/MyTeam/CreateTeam";
import TeamViewPage from "./Pages/Home/MyTeam/TeamViewPage";
import TeamGeofenceView from "./Pages/Home/MyTeam/Geofence/GeofenceView";
import TeamGeofenceSetup from "./Pages/Home/MyTeam/Geofence/GeofenceSetup";
import TeamMemberViewPageIndex from "./Pages/Home/MyTeam/teamMemberViewPage";
import LocationIndex from "./Pages/Home/Location";
import CreateLocation from "./Pages/Home/Location/CreateLocation";
import LocationDetailsPage from "./Pages/Home/Location/LocationDetailsPage";
import LocationReassign from "./Pages/Home/Location/LocationReassign";
import GoogleApiWrapper from "./Pages/Home/Maps/index";
import ViewRoute from "./Pages/Home/Maps/viewRoute";
import ViewLocationRoute from "./Pages/Home/Maps/viewLocationRoute";
import Chatindex from "./Pages/Home/Chat/chatindex";
import SubscriptionsHome from "./Pages/Home/Subscription/SubscriptionsHome";
import MyProfileIndex from "./Pages/Home/MyProfile";
import ResetPassword from "./Pages/Home/MyProfile/ResetPassword";
import AddressHome from "./Pages/Home/address/AddressHome";
import AddressMaps from "./Pages/Home/address/AddressMaps";
import AddressUsage from "./Pages/Home/address/AddressUsage";
import ReportIndex from "./Pages/Home/Reports";
import ViewReport from "./Pages/Home/Reports/viewReport";
import ExpenseApprovals from "./Pages/Home/ExpenseApprovals";
import OrderSummary from "./Pages/Home/orderSummary/OrderSummary";

// --- Header Nav / Info Pages ---
import Contactus from "./Pages/Home/HeaderNavPages/Contactus";
import Aboutus from "./Pages/Home/HeaderNavPages/Aboutus";
import Feedback from "./Pages/Home/HeaderNavPages/Feedback";
import PoliciesIndex from "./Pages/Home/HeaderNavPages/PoliciesIndex";
import Referearn from "./Pages/Home/ReferEarn/Referearn";
import FaqSection from "./Pages/Home/FaqSection/FaqSection";
import Demo from "./Pages/Home/Demo/demo";

// --- Shared Components ---
import NotificationsPage from "./components/notificationsPage";
// import MarkHoliday from "./Pages/Home/HolidayMarker/MarkHoliday";

// --- Public (no auth, no session redirect) ---
import ShareLocationPage from "./Pages/ShareLocation";

const Router = createBrowserRouter([
  // ── Public share link ────────────────────────────────────────────────────
  // Deliberately NOT nested under GuestLayout: GuestLayout redirects
  // already-logged-in visitors to /home, which would break this link for a
  // team leader who is logged in when they open it. This page has no auth
  // state at all — it must render the same for a signed-out visitor and a
  // signed-in one.
  {
    path: "/share-location/:token",
    element: <ShareLocationPage />,
  },

  // ── Guest (unauthenticated) ──────────────────────────────────────────────
  {
    path: "/",
    element: <GuestLayout />,
    children: [
      { path: "/", element: <Navigate to="/login" /> },
      { path: "/login", element: <EmailLogin /> },
      { path: "/otp_login", element: <OtpLogin /> },
      { path: "/signup", element: <SelectLanguage /> },
      { path: "/delete_leader", element: <DeleteLeader /> },
      { path: "/your-best", element: <SelectRole /> },
      { path: "/personal-details", element: <PersonalDetails /> },
      { path: "/otp1", element: <Otp1 /> },
      { path: "/otp2", element: <Otp2 /> },
      { path: "/register-company", element: <RegisterCompany /> },
      { path: "/set-password", element: <SetPassword /> },
    ],
  },

  // ── Functional (post-register transitional) ──────────────────────────────
  {
    path: "/",
    element: <FunctionalLayout />,
    children: [
      { path: "/register-success", element: <RegisteredSuccessfully /> },
      { path: "/order_summary", element: <OrderSummary /> },
      { path: "/payment_success", element: <PaymentSuccess /> },
    ],
  },

  // ── Default (authenticated app) ─────────────────────────────────────────
  {
    path: "/",
    element: <DefaultLayout />,
    children: [
      { path: "/", element: <Navigate to="/home" /> },

      // Dashboard
      { path: "/home", element: <Dashboard /> },
      { path: "/notifications", element: <NotificationsPage /> },
      { path: "/demo", element: <Demo /> },

      // Teams
      { path: "/my-team", element: <MyTeam /> },
      { path: "/my-team/create-team", element: <CreateTeam /> },
      { path: "/my-team/update/:name", element: <CreateTeam /> },
      { path: "/my-team/:name", element: <TeamViewPage /> },
      { path: "/my-team/:name/geofence", element: <TeamGeofenceView /> },
      { path: "/my-team/:name/geofence/setup", element: <TeamGeofenceSetup /> },
      { path: "/my-team/:name/:id", element: <TeamMemberViewPageIndex /> },

      // Locations
      { path: "/location", element: <LocationIndex /> },
      { path: "/location/create-location", element: <CreateLocation /> },
      { path: "/location/update/:name", element: <CreateLocation /> },
      { path: "/location/:name", element: <LocationDetailsPage /> },
      { path: "/location/reassign/:name", element: <LocationReassign /> },

      // Maps
      { path: "/maps", element: <GoogleApiWrapper /> },
      { path: "/maps/view-route/:id", element: <ViewRoute /> },
      { path: "/maps/location-view-route/:id", element: <ViewLocationRoute /> },

      // Subscriptions
      { path: "/subscriptions", element: <SubscriptionsHome /> },
      { path: "/select-subscription", element: <RegisteredSuccessfully /> },
      { path: "/referal-subscription", element: <RegisteredSuccessfully /> },
      { path: "/upgrade-plan/:id", element: <RegisteredSuccessfully /> },
      { path: "/order_summary_forall/:id", element: <OrderSummaryForAll /> },
      // { path: "/mark-a-holiday", element: <MarkHoliday /> },

      // Profile & Account
      { path: "/my-profile", element: <MyProfileIndex /> },
      { path: "/reset-password", element: <ResetPassword /> },

      // Chat & Comms
      { path: "/chat", element: <Chatindex /> },

      // Address
      { path: "/address", element: <AddressHome /> },
      // `usage/:id`, not `:id/usage`: detail pages here read their id by
      // taking the LAST pathname segment, which `:id/usage` would break.
      // Matches the existing /maps/view-route/:id shape.
      { path: "/address/usage/:id", element: <AddressUsage /> },
      { path: "/address-maps", element: <AddressMaps /> },

      // Reports
      { path: "/reports", element: <ReportIndex /> },
      { path: "/reports/view-report", element: <ViewReport /> },

      // Expenses
      { path: "/expense-approvals", element: <ExpenseApprovals /> },

      // Info / Nav Pages
      { path: "/contact-us", element: <Contactus /> },
      { path: "/about-us", element: <Aboutus /> },
      { path: "/feedback", element: <Feedback /> },
      { path: "/policies", element: <PoliciesIndex /> },
      { path: "/refer-earn", element: <Referearn /> },
      { path: "/help-faq", element: <FaqSection /> },
    ],
  },
]);

export default Router;
